import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * MountOnView — defers rendering of children until the placeholder enters
 * the viewport. Unlike LazyOnView, this does NOT code-split: it just delays
 * the React subtree's first render. Use for inline sections whose JS is
 * already in the main bundle but whose render cost (cards, queries, large
 * trees) should not compete with the hero.
 */
export function MountOnView({
  children,
  fallback = null,
  rootMargin = "1200px 0px",
  minHeight = "1px",
}: {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
  minHeight?: string | number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);

    // Red de seguridad: si el usuario hace scroll muy rápido (o el observer
    // no dispara a tiempo), montamos igualmente poco después del primer paint
    // para que nunca aparezca un hueco en blanco.
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (h: number) => void;
    };
    const idle = w.requestIdleCallback
      ? w.requestIdleCallback(() => setShow(true), { timeout: 2500 })
      : w.setTimeout(() => setShow(true), 2000);


    return () => {
      io.disconnect();
      if (w.cancelIdleCallback) w.cancelIdleCallback(idle);
      else clearTimeout(idle);
    };

  }, [rootMargin, show]);

  // El minHeight se mantiene SIEMPRE: reserva el espacio antes y después de
  // montar, evitando el colapso de altura (salto/flash blanco) en el momento
  // exacto en que el contenido aparece.
  return (
    <div ref={ref} style={{ minHeight }}>
      {show ? children : fallback}
    </div>
  );
}

